import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
    Intent, SlotForm, emptySlot,
    EntityValueSelectionType, EntityValueSelectionTypes
} from "@/model";
import { useEntityTags } from "@/query/entity";
import { useUpdateIntent } from "@/query/intent";
import { useAgentId, useModal } from "@/hook";
import { 
    DropdownGroup, DropdownButton, DropdownItem, DropdownItems,
    Toggle, Typeahead, TypeaheadOptions, TypeaheadInput
} from "@/component/input";
import Button from "@/component/Button";
import Tooltip from "@/component/Tooltip";
import { Modal } from "@/component/modal";
import { errorToast, successToast } from "@/component/Toast";
import { AddIcon, DeleteIcon } from "@/assets/svgs/icons";


export const INTENT_SLOT_MODAL_ID = "intent-slot-modal-id";

type IntentSlotModalData = {
    intent: Intent,
    slots: SlotForm[]
}

const IntentSlotModal: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const agentId = useAgentId();
    const data: IntentSlotModalData | undefined = modal.getData(INTENT_SLOT_MODAL_ID);

    const [validated, setValidated] = useState<boolean>(false);
    const [slots, setSlots] = useState<SlotForm[]>([]);

    const { mutateAsync: updateIntent, isPending: isUpdating } = useUpdateIntent(
        agentId,
        data?.intent.skill_id,
        data?.intent.id
    );
    const { data: entityTags } = useEntityTags(data?.intent.agent_id);

    const updateSlot = (slot: SlotForm, index: number) => {
        setSlots(prev => [
            ...prev.slice(0, index),
            slot,
            ...prev.slice(index + 1)
        ]);
    };

    const deleteSlot = (index: number) => {
        setSlots(prev => [
            ...prev.slice(0, index),
            ...prev.slice(index + 1)
        ]);
    };

    const onClickConfirm = async () => {
        setValidated(true);
        const validation = slots.every(slot => !!slot.parameter && !!slot.entity_tags.length);
        if (!validation) {
            return;
        }

        await updateIntent({ slot_info: slots })
            .then(() => {
                modal.close(INTENT_SLOT_MODAL_ID);
                successToast(intl.formatMessage({ id: "t003" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t004" }));
            });
    };

    useEffect(() => {
        setSlots(!data?.slots.length ? [emptySlot] : [...data.slots]);
    }, [data, data?.slots]);

    return (
        <Modal id={INTENT_SLOT_MODAL_ID}
               size="xl"
               title={
                   <div className="flex gap-2 items-end">
                        <div className="text-black-500 text-[16px] font-[600] leading-[1.5]">
                            {intl.formatMessage({ id: "i03031" })}
                        </div>
                        <div className="text-black-400 text-[12px] font-[400] leading-[1.5]">
                            {intl.formatMessage({ id: "i03032" })}
                        </div>
                   </div>
               }
               onClose={() => setValidated(false)}
        >
            <div className="modal-body flex-1 min-h-0">
                <div className="flex-1 min-h-0 flex flex-col gap-4">
                    <div className="flex gap-1.5 items-center text-black-500 text-[14px] font-[500] leading-[1.5]">
                        {intl.formatMessage({ id: "i03033" }, { intentName: data?.intent.name })}
                        <Tooltip message={(
                                    <div className="flex flex-col">
                                        <div>{intl.formatMessage({ id: "i03045" })}</div>
                                        <div>{`• ${intl.formatMessage({ id: "i03046" })}`}</div>
                                        <div>{`• ${intl.formatMessage({ id: "i03047" })}`}</div>
                                        <div>{`• ${intl.formatMessage({ id: "i03048" })}`}</div>
                                    </div>
                                )}
                        />
                    </div>

                    <div role="table"
                         className="selection-table grid-table-intent-slot max-h-[70vh]"
                    >
                        <div role="rowgroup"
                             className="selection-table-thead border-b-[2px] border-black-200 text-black-500 text-[14px] font-[500] leading-[1.4] h-[52px]"
                        >
                            <div role="columnheader"
                                 className="selection-table-th justify-center"
                            >
                                {intl.formatMessage({ id: "i03034" })}
                            </div>
                            <div role="columnheader"
                                 className="selection-table-th justify-center"
                            >
                                {intl.formatMessage({ id: "i03035" })}
                            </div>
                            <div role="columnheader"
                                 className="selection-table-th justify-center"
                            >
                                {intl.formatMessage({ id: "i03036" })}
                            </div>
                            <div role="columnheader"
                                 className="selection-table-th justify-center"
                            >
                                {intl.formatMessage({ id: "i03037" })}
                            </div>
                            <div role="columnheader"
                                 className="selection-table-th justify-center"
                            >
                                {intl.formatMessage({ id: "i03039" })}
                            </div>
                            <div role="columnheader"
                                 className="selection-table-th justify-center"
                            >
                                {intl.formatMessage({ id: "i03041" })}
                            </div>
                            <div role="columnheader"
                                 className="selection-table-th justify-center"
                            >
                                {intl.formatMessage({ id: "i03043" })}
                            </div>
                        </div>
                        <div role="rowgroup"
                             className="selection-table-tbody"
                        >
                            {slots.map((slot, index) => (
                                <SlotFragment key={`${index}_${slot.key}`}
                                              validated={validated}
                                              isOnlyOne={slots.length === 1}
                                              slot={slot}
                                              entityTags={entityTags || []}
                                              onChange={slot => updateSlot(slot, index)}
                                              onDelete={() => deleteSlot(index)}
                                />
                            ))}
                        </div>
                    </div>

                    <Button size="sm" icon
                            variant="outline-primary"
                            className="-mt-2 py-3"
                            onClick={() => setSlots(prev => [...prev, emptySlot])}
                    >
                        <AddIcon className="text-chat42 size-3.5 stroke-2" />
                    </Button>
                </div>

                <div className="modal-btn-group">
                    <Button variant="secondary"
                            size="sm"
                            disabled={isUpdating}
                            onClick={() => modal.close(INTENT_SLOT_MODAL_ID)}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                    <Button variant="primary"
                            size="sm"
                            disabled={isUpdating}
                            onClick={onClickConfirm}
                    >
                        {intl.formatMessage({ id: "b0001" })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

interface SlotFragmentProps {
    slot: SlotForm;
    entityTags: string[];
    validated: boolean;
    isOnlyOne: boolean;
    onChange: (slot: SlotForm) => void;
    onDelete: () => void;
}

const SlotFragment: React.FC<SlotFragmentProps> = (props) => {
    const {
        slot,
        entityTags,
        isOnlyOne,
        validated,
        onChange,
        onDelete
    } = props;
    const intl = useIntl();

    const [slotSearchKeyword, setSlotSearchKeyword] = useState<string>("");

    const entitySelectionLabel: Record<EntityValueSelectionType, string> = {
        keyword: intl.formatMessage({ id: "i03063" }),
        synonym: intl.formatMessage({ id: "i03064" }),
        mapping_value: intl.formatMessage({ id: "i03065" }),
        nothing: intl.formatMessage({ id: "i03066" }),
    };

    const onClickDeleteBtn = () => {
        onDelete();
    };

    const onToggleRequired = (required: boolean) => {
        onChange({
            ...slot,
            required
        });
    };
    const onToggleIsList = (isList: boolean) => {
        onChange({
            ...slot,
            is_list: isList
        });
    };

    const onChangeParameter = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({
            ...slot,
            parameter: e.target.value,
        });
    };

    const onChangeEntityTags = (tag?: string | string[]) => {
        let tagArray = tag;
        if (!Array.isArray(tag)) {
            tagArray = tag ? [tag] : [];
        }
        onChange({
            ...slot,
            entity_tags: tagArray as string[]
        });
    };

    const onSelectEntityValueSelection = (item: EntityValueSelectionType) => {
        onChange({
            ...slot,
            entity_value_selection: item
        });
    };

    const onChangeDefault = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({
            ...slot,
            default: e.target.value,
        });
    };

    const filteredEntityTags = useMemo(() => {
        if (!slotSearchKeyword) {
            return entityTags;
        }
        return entityTags.filter(tag => tag.toLowerCase().includes(slotSearchKeyword.toLowerCase()));
    }, [entityTags, slotSearchKeyword]);

    return (
        <div role="row"
             className="selection-table-tr no-hover"
        >
            <div role="cell"
                 className="selection-table-td min-h-[69px] py-4 px-2 flex justify-center items-center"
            >
                {!isOnlyOne && (
                    <Button className="flex justify-center items-center"
                            onClick={onClickDeleteBtn}
                    >
                        <DeleteIcon className="size-4.5 text-point-red stroke-1" />
                    </Button>
                )}
            </div>
            <div role="cell"
                 className="selection-table-td min-h-[69px] py-4 px-2 flex justify-center items-center"
            >
                <Toggle defaultStatus={slot.required}
                        onToggle={onToggleRequired}
                />
            </div>
            <div role="cell"
                 className="selection-table-td min-h-[69px] py-4 px-2 flex justify-center items-center"
            >
                <Toggle defaultStatus={slot.is_list}
                        onToggle={onToggleIsList}
                />
            </div>
            <div role="cell"
                 className="selection-table-td min-h-[69px] py-4 px-2 flex justify-center items-center"
            >
                <input type="text"
                       required={validated}
                       className="input-default"
                       onChange={onChangeParameter}
                       placeholder={intl.formatMessage({ id: "i03038" })}
                       defaultValue={slot.parameter}
                />
            </div>
            <div role="cell"
                 className="selection-table-td min-h-[69px] py-4 px-2 flex justify-center items-center"
            >
                {slot.is_list ? (
                    <Typeahead multiple
                               selection={slot.entity_tags}
                               onChange={onChangeEntityTags}
                    >
                        <TypeaheadInput invalid={validated && !entityTags.length}
                                        selection={slot.entity_tags}
                                        onBlur={() => setSlotSearchKeyword("")}
                                        placeholder={intl.formatMessage({ id: "i03040" })}
                                        onChange={e => setSlotSearchKeyword(e.target.value)}
                        />
                        <TypeaheadOptions options={filteredEntityTags} />
                    </Typeahead>
                ) : (
                    <Typeahead selection={slot.entity_tags[0] ?? ""}
                               onChange={onChangeEntityTags}
                    >
                        <TypeaheadInput invalid={validated && !entityTags.length}
                                        selection={slot.entity_tags[0]}
                                        onBlur={() => setSlotSearchKeyword("")}
                                        placeholder={intl.formatMessage({ id: "i03040" })}
                                        onChange={e => setSlotSearchKeyword(e.target.value)}
                        />
                        <TypeaheadOptions options={filteredEntityTags} />
                    </Typeahead>
                )}
            </div>
            <div role="cell"
                 className="selection-table-td min-h-[69px] py-4 px-2 flex justify-center items-center"
            >
                <DropdownGroup>
                    <DropdownButton>
                        {entitySelectionLabel[slot.entity_value_selection]}
                    </DropdownButton>
                    <DropdownItems>
                        {EntityValueSelectionTypes.map(item => (
                            <DropdownItem key={slot.key}
                                          onClick={() => onSelectEntityValueSelection(item)}
                            >
                                {entitySelectionLabel[item]}
                            </DropdownItem>
                        ))}
                    </DropdownItems>
                </DropdownGroup>
            </div>
            <div role="cell"
                 className="selection-table-td min-h-[69px] py-4 px-2 flex justify-center items-center"
            >
                <input type="text"
                       className="input-default"
                       onChange={onChangeDefault}
                       placeholder={intl.formatMessage({ id: "i03044" })}
                       defaultValue={slot.default}
                />
            </div>
        </div>
    );
};

export default IntentSlotModal;
